import React, { useEffect, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Header } from './Header';
import { Footer } from './Footer';
import { SEO } from './SEO';
import * as Icons from 'lucide-react';
import { legalContent } from '../data/legalContent';

/*
 * Unified, styled renderer for all legal pages (Terms / Privacy / Cookie / Refund).
 * Content lives in src/data/legalContent.js as structured data (RU + EN):
 *   { hero:{icon,title,subtitle,updated}, intro?, sections:[{id,icon,title,blocks:[...]}] }
 * Block types: p | list | callout(variant: warn|info|ok|rule) | table | steps | kb(links)
 */

const Ico = ({ name, className }) => {
  const C = Icons[name] || Icons.FileText;
  return <C className={className} />;
};

const CALLOUT = {
  warn: { wrap: 'bg-amber-50 border-amber-400', icon: 'text-amber-500', title: 'text-amber-900', ic: 'AlertTriangle' },
  danger: { wrap: 'bg-red-50 border-red-400', icon: 'text-red-500', title: 'text-red-900', ic: 'Ban' },
  info: { wrap: 'bg-blue-50 border-blue-400', icon: 'text-blue-500', title: 'text-blue-900', ic: 'Info' },
  ok: { wrap: 'bg-emerald-50 border-emerald-400', icon: 'text-emerald-500', title: 'text-emerald-900', ic: 'CheckCircle' },
  rule: { wrap: 'bg-slate-50 border-slate-400', icon: 'text-slate-500', title: 'text-slate-900', ic: 'Scale' },
};

const Block = ({ b }) => {
  if (b.t === 'p') return <p className="text-gray-700 leading-relaxed mb-4" dangerouslySetInnerHTML={{ __html: b.c }} />;
  if (b.t === 'list')
    return (
      <ul className="space-y-2.5 mb-5">
        {b.items.map((it, i) => (
          <li key={i} className="flex items-start gap-3 text-gray-700">
            <Ico name={b.icon || 'Check'} className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <span dangerouslySetInnerHTML={{ __html: it }} />
          </li>
        ))}
      </ul>
    );
  if (b.t === 'steps')
    return (
      <ol className="space-y-3 mb-5">
        {b.items.map((it, i) => (
          <li key={i} className="flex items-start gap-3">
            <span className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-600 text-white text-sm font-bold flex items-center justify-center">{i + 1}</span>
            <span className="text-gray-700 pt-0.5" dangerouslySetInnerHTML={{ __html: it }} />
          </li>
        ))}
      </ol>
    );
  if (b.t === 'callout') {
    const c = CALLOUT[b.v] || CALLOUT.info;
    return (
      <div className={`${c.wrap} border-l-4 rounded-r-xl p-4 sm:p-5 mb-5`}>
        <div className="flex items-start gap-3">
          <Ico name={b.icon || c.ic} className={`w-6 h-6 ${c.icon} flex-shrink-0 mt-0.5`} />
          <div>
            {b.title && <div className={`font-semibold mb-1 ${c.title}`}>{b.title}</div>}
            <div className="text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: b.c }} />
          </div>
        </div>
      </div>
    );
  }
  if (b.t === 'table')
    return (
      <div className="overflow-x-auto mb-5 rounded-xl border border-gray-200">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>{b.head.map((h, i) => <th key={i} className="text-left font-semibold text-gray-900 px-4 py-3">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {b.rows.map((r, i) => (
              <tr key={i}>{r.map((cell, j) => <td key={j} className="px-4 py-3 text-gray-700" dangerouslySetInnerHTML={{ __html: cell }} />)}</tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  if (b.t === 'kb')
    return (
      <div className="flex flex-wrap gap-3 mb-5">
        {b.links.map((l, i) => (
          <a key={i} href={l.href} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-blue-50 hover:bg-blue-100 text-blue-700 font-medium transition-colors border border-blue-100">
            <Ico name={l.icon || 'BookOpen'} className="w-4 h-4" />
            {l.label}
            <Icons.ArrowRight className="w-4 h-4" />
          </a>
        ))}
      </div>
    );
  return null;
};

export const LegalPage = ({ page }) => {
  const { language } = useLanguage();
  const [active, setActive] = useState(null);
  const data = (legalContent[page] && legalContent[page][language]) || legalContent[page].en;

  useEffect(() => { window.scrollTo(0, 0); }, [page]);

  return (
    <div className="min-h-screen bg-white">
      <SEO page={page} />
      <Header />

      {/* Hero */}
      <section className="relative py-16 sm:py-20 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-blue-400 rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 rounded-2xl mb-6 ring-1 ring-white/20">
            <Ico name={data.hero.icon} className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">{data.hero.title}</h1>
          <p className="text-lg text-blue-200 max-w-2xl mx-auto">{data.hero.subtitle}</p>
          <div className="mt-6 inline-flex items-center gap-2 text-sm text-blue-300 bg-white/5 px-4 py-1.5 rounded-full">
            <Icons.Clock className="w-4 h-4" /> {data.hero.updated}
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="py-12 sm:py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-[260px_1fr] gap-8">
          {/* TOC */}
          <aside className="hidden lg:block">
            <div className="sticky top-24">
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">{data.tocLabel}</div>
              <nav className="space-y-1">
                {data.sections.map((s) => (
                  <a key={s.id} href={`#${s.id}`}
                     onClick={() => setActive(s.id)}
                     className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${active === s.id ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-white hover:text-blue-700'}`}>
                    <Ico name={s.icon} className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">{s.title}</span>
                  </a>
                ))}
              </nav>
            </div>
          </aside>

          {/* Content */}
          <div className="min-w-0">
            {data.intro && (
              <div className="bg-blue-50 border border-blue-100 rounded-2xl p-5 sm:p-6 mb-8">
                <p className="text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: data.intro }} />
              </div>
            )}
            <div className="space-y-6">
              {data.sections.map((s) => (
                <div key={s.id} id={s.id} className="scroll-mt-24 bg-white rounded-2xl border border-gray-100 shadow-sm p-6 sm:p-8">
                  <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-100">
                    <div className="w-11 h-11 rounded-xl bg-blue-600/10 flex items-center justify-center flex-shrink-0">
                      <Ico name={s.icon} className="w-6 h-6 text-blue-600" />
                    </div>
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900">{s.title}</h2>
                  </div>
                  {s.blocks.map((b, i) => <Block key={i} b={b} />)}
                </div>
              ))}
            </div>
            {data.footerNote && (
              <p className="text-center text-sm text-gray-400 mt-8" dangerouslySetInnerHTML={{ __html: data.footerNote }} />
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};
